import React from 'react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Trash2, Plus } from 'lucide-react';

const OPTION_KEYS = ['a', 'b', 'c', 'd'];

/**
 * Klick-basierter Quiz-Editor mit Radiobuttons für die richtige Antwort.
 * Ersetzt die JSON-Textarea für Nutzer, die keine JSON-Syntax kennen.
 *
 * value: Array von Frage-Objekten { id, question, options: [{id,text}], correct_option_id, explanation }
 * onChange: (nextArray) => void
 */
export const QuizQuestionEditor = ({ value = [], onChange }) => {
  const questions = Array.isArray(value) ? value : [];

  const updateQuestion = (idx, patch) => {
    const next = questions.map((q, i) => (i === idx ? { ...q, ...patch } : q));
    onChange(next);
  };

  const updateOption = (qIdx, optIdx, text) => {
    const next = questions.map((q, i) => {
      if (i !== qIdx) return q;
      const options = OPTION_KEYS.map((k, j) => ({
        id: k,
        text: j === optIdx ? text : (q.options?.[j]?.text ?? ''),
      }));
      return { ...q, options };
    });
    onChange(next);
  };

  const addQuestion = () => {
    const nextId = `kc-${questions.length + 1}`;
    const newQ = {
      id: nextId,
      question: '',
      options: OPTION_KEYS.map((k) => ({ id: k, text: '' })),
      correct_option_id: 'a',
      explanation: '',
    };
    onChange([...questions, newQ]);
  };

  const removeQuestion = (idx) => {
    onChange(questions.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4" data-testid="quiz-editor">
      {questions.length === 0 && (
        <p className="text-sm text-gray-400 italic">Noch keine Wissens-Check-Frage angelegt.</p>
      )}

      {questions.map((q, qIdx) => (
        <div
          key={q.id || qIdx}
          className="border border-yellow-600/25 bg-gray-950/50 rounded-lg p-4 space-y-3"
          data-testid={`quiz-question-${qIdx}`}
        >
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <Label className="text-gray-300 text-xs">Frage {qIdx + 1}</Label>
              <Input
                value={q.question || ''}
                onChange={(e) => updateQuestion(qIdx, { question: e.target.value })}
                placeholder="Deine Wissens-Frage…"
                className="bg-gray-900 border-yellow-600/25 text-white"
                data-testid={`quiz-question-text-${qIdx}`}
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => removeQuestion(qIdx)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 mt-5"
              data-testid={`quiz-remove-question-${qIdx}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <div>
            <Label className="text-gray-300 text-xs mb-1 block">
              Antworten (Radio-Button auswählen = richtige Antwort)
            </Label>
            <div className="space-y-2">
              {OPTION_KEYS.map((key, optIdx) => (
                <div key={key} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={`correct-${qIdx}`}
                    checked={q.correct_option_id === key}
                    onChange={() => updateQuestion(qIdx, { correct_option_id: key })}
                    className="w-4 h-4 accent-green-500 shrink-0"
                    data-testid={`quiz-correct-${qIdx}-${key}`}
                    title={`Antwort ${key.toUpperCase()} als richtig markieren`}
                  />
                  <span className={`text-sm font-mono font-semibold w-6 ${q.correct_option_id === key ? 'text-green-400' : 'text-gray-500'}`}>
                    {key.toUpperCase()}
                  </span>
                  <Input
                    value={q.options?.[optIdx]?.text ?? ''}
                    onChange={(e) => updateOption(qIdx, optIdx, e.target.value)}
                    placeholder={`Antwort ${key.toUpperCase()}…`}
                    className={`bg-gray-900 border-yellow-600/25 text-white text-sm ${q.correct_option_id === key ? 'border-green-500/50 ring-1 ring-green-500/30' : ''}`}
                    data-testid={`quiz-option-${qIdx}-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-gray-300 text-xs">Erklärung (wird nach Antwort angezeigt)</Label>
            <Input
              value={q.explanation || ''}
              onChange={(e) => updateQuestion(qIdx, { explanation: e.target.value })}
              placeholder="Warum ist diese Antwort richtig?"
              className="bg-gray-900 border-yellow-600/25 text-white text-sm"
              data-testid={`quiz-explanation-${qIdx}`}
            />
          </div>
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        onClick={addQuestion}
        className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
        data-testid="quiz-add-question-btn"
      >
        <Plus className="h-4 w-4 mr-1" /> Neue Frage hinzufügen
      </Button>
    </div>
  );
};

export default QuizQuestionEditor;
